import React, {useEffect, useMemo, useState} from 'react';
import {useMediaQuery} from 'react-responsive';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {get} from '../../api/api';
import {uriGetDeducts, uriGetTopV2Coverages} from '../../api/endpoints';
import Header1 from '../../components/headers/header1';
import {Column, Row} from '../../components/layout';
import colors from '../../util/colors';
import {functionalitiesAliasMap, strings} from '../../util/strings';
import CoveragesList from './components/CoveragesList';
import * as fonts from '../../util/fonts';
import './styles.scss';
import AppSpinner from '../../components/app-spinner';
import AppDownloadButton from '../../components/app-download-button';
import attachfiles from '../../assets/attach-files.svg';
import NewCoverageDetails from './components/NewCoverageDetails';
import Icon from '../../components/icon';
import {Icons} from '../../util/icons';

interface CoverageProps {
  selectedBu: any;
  labels: typeof strings.pt.myCoverages;
  buStyles: any;
  titular: any;
  busFuncMap: any;
}

export interface CoveragesItem {
  deductMemAmt: number;
  idReg: number;
  limitCount: number;
  spentCount: string;
  availableAmt: number;
  catCode: string;
  catName: string;
  catDesc?: string;
  copayName: string;
  deductType: string;
  limitAmt: number;
  limitSvcNbr: string;
  network: string;
  priority: string;
  amtSpent: number;
  copayValue: any;
  currency: string;
}

export interface Franchise {
  alias: any;
  displayText: string;
  tipodeFranquiacodigoRIOS: string;
  descricaoCurta: string;
  descricaoLonga: string;
}

export interface Deduct {
  id: number;
  title: string;
  status: string;
  deductType: string;
  buAlias: string;
  deductDetails: {
    id: number;
    deductAlias: string;
    languageAlias: string;
    shortDescription: string;
    longDescription: string;
  }[];
  deductAlias: string;
}

function MyCoverages({
  selectedBu,
  labels,
  buStyles,
  titular,
  busFuncMap,
}: CoverageProps) {
  const [showPolicyNumber, setShowPolicyNumber] = useState(
    labels?.show_policy_number
  );
  const [coveragesList, setCoveragesList] = useState([]);
  const [selectedItem, setSelectedItem] = useState<CoveragesItem | any>({});
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [coverageDetails, setCoverageDetails] = useState([]);
  const [errorFetching, setErrorFetching] = useState(false);
  const [errorFetchingDetail, setErrorFetchingDetail] = useState(false);
  const [deducts, setDeducts] = useState();
  const [listType, setListType] = useState(
    labels?.listType || strings.pt.myCoverages.listType
  );
  const [pdfUrl, setPdfUrl] = useState(
    labels?.pdfUrl || strings.pt.myCoverages.pdfUrl
  );
  const [listTypeReady, setListTypeReady] = useState(false);

  const onWebMobile = window.innerWidth <= 768;

  const getCoverages = async () => {
    setIsLoading(true);
    try {
      let res: any;

      res = await get(
        uriGetTopV2Coverages(
          selectedBu.buId,
          selectedBu.memId,
          selectedBu.benNbr
        )
      );

      setCoveragesList(res.data.items);
    } catch (err: any) {
      setCoveragesList([]);
      setErrorFetching(true);
    } finally {
      setIsLoading(false);
      setListTypeReady(false);
    }
  };

  const updateCoverages = () => {
    if (listType === 'LISTA' && listTypeReady) {
      getCoverages();
    }
  };

  useEffect(() => {
    window.scrollTo(0,0)
  }, [])

  useEffect(() => {
    get(uriGetDeducts(selectedBu.buId)).then((resp: any) => {
      setDeducts(resp?.data?.items);
    });
  }, [selectedBu.buId]);

  useEffect(() => {
    listType === 'PDF' && setListTypeReady(true);
  }, [listType]);

  useEffect(() => {
    updateCoverages();
  }, [listType, listTypeReady]);

  useMemo(() => {
    const coveragesStrings: any = busFuncMap[selectedBu.buId].find(
      (el: any) => el.featureTemplateAlias === functionalitiesAliasMap.coverages
    );
    if (coveragesStrings) {
      const listTypeString = coveragesStrings.labels?.find(
        (label: any) => label.labelTemplateAlias === 'listType'
      );
      const showPolicyNumberString = coveragesStrings.labels?.find(
        (label: any) => label.labelTemplateAlias === 'show_policy_number'
      );
      if (listTypeString) {
        setListType(listTypeString.text);
      } else {
        setListType(strings.pt.myCoverages.listType);
      }
      const pdfUrlString = coveragesStrings.labels?.find(
        (label: any) => label.labelTemplateAlias === 'pdfUrl'
      );
      setShowPolicyNumber(
        showPolicyNumberString.text || strings.pt.myCoverages.show_policy_number
      );
      setPdfUrl(pdfUrlString.text || strings.pt.myCoverages.pdfUrl);
    }
    setListTypeReady(true);
  }, [selectedBu.memId]);

  const isResponsive = useMediaQuery({maxDeviceWidth: 768});

  const renderCoverages = () => {
    return (
      <Column className="coverages-list-column">
        {listType === 'LISTA' && (
          <CoveragesList
            coveragesList={coveragesList}
            setCoverageDetails={setCoverageDetails}
            setSelectedItem={setSelectedItem}
            setShowModal={setShowModal}
            setErrorFetchingDetail={setErrorFetchingDetail}></CoveragesList>
        )}
        {listType === 'PDF' && (
          <a target="_blank" href={pdfUrl} className="anchor m-0">
            <Row className="reimbursement-success-file coverages-pdf-row">
              <img className="irs-declaration-icon" src={attachfiles} />
              <Column
                className="reimbursement-success-file-title"
                style={{...fonts.M, ...fonts.Medium}}>
                {labels?.myCovers}
              </Column>
              <AppDownloadButton
                color={buStyles?.primaryColor || colors.blue}
              />
            </Row>
          </a>
        )}
      </Column>
    );
  };

  return (
    <>
      <Column className="coverages-container">
        <Row className="coverages-titlerow coverages-row">
          <Column className="coverages-titlecolumn">
            <Header1
              className="coverages-heading1"
              style={
                onWebMobile
                  ? {...fonts.ML, fontSize: '26px', ...fonts.Bold}
                  : {...fonts.XL, ...fonts.Bold}
              }>
              {labels?.myCovers}
            </Header1>
            <div
              className="coverages-subtitle"
              style={onWebMobile ? {...fonts.SM} : {...fonts.M}}>
              {labels?.coverages_subtitle}
            </div>
          </Column>
        </Row>
        <Row className="coverages-row">
          <Column
            className="coverages-container coverages-bgbox"
            style={{background: colors.backgroundGrey}}>
            <Row className="coverages-titular-info">
              <div
                style={
                  onWebMobile
                    ? {
                        ...fonts.M,
                        fontSize: '14px',
                        paddingRight: '5px',
                        textAlign: 'left',
                      }
                    : {
                        ...fonts.M,
                        fontSize: '17px',
                        paddingRight: '10px',
                      }
                }>
                {labels?.policyHolderName}:
              </div>
              <strong
                style={
                  onWebMobile
                    ? {
                        ...fonts.M,
                        ...fonts.Bold,
                        fontSize: '14px',
                        textAlign: 'right',
                      }
                    : {
                        ...fonts.M,
                        ...fonts.Bold,
                        fontSize: '17px',
                      }
                }>
                {titular?.memberName || titular?.name}
              </strong>
            </Row>
            {isResponsive ? (
              <>
                {showPolicyNumber === 'true' && (
                  <Row className="coverages-titular-info">
                    <div
                      style={{
                        ...fonts.M,
                        ...fonts.Medium,
                        fontSize: '14px',
                      }}>
                      {labels?.policyNumber}:
                    </div>
                    <strong
                      style={{
                        ...fonts.M,
                        ...fonts.Bold,
                        fontSize: '14px',
                        textAlign: 'end',
                      }}>
                      {selectedBu.grpId}
                    </strong>
                  </Row>
                )}
                <Row className="coverages-titular-info">
                  {' '}
                  <div
                    style={{
                      ...fonts.M,
                      ...fonts.Medium,
                      fontSize: '14px',
                    }}>
                    {labels?.policy}:
                  </div>
                  <strong
                    style={{
                      ...fonts.M,
                      ...fonts.Bold,
                      fontSize: '14px',
                      textAlign: 'end',
                    }}>
                    {selectedBu.grpName}
                  </strong>
                </Row>
              </>
            ) : (
              <Row
                className="coverages-titular-info"
                style={{
                  justifyContent:
                    showPolicyNumber === 'true'
                      ? 'space-between'
                      : 'flex-start',
                  textAlign: 'start',
                }}>
                {showPolicyNumber === 'true' && (
                  <>
                    <div
                      style={{
                        ...fonts.M,
                        fontSize: '17px',
                        paddingRight: '10px',
                      }}>
                      {labels?.policyNumber}:
                    </div>
                    <strong
                      style={{
                        ...fonts.M,
                        ...fonts.Bold,
                        fontSize: '17px',
                        paddingRight: '10px',
                      }}>
                      {selectedBu.grpId}
                    </strong>
                  </>
                )}
                <div
                  style={{
                    ...fonts.M,
                    fontSize: '17px',
                    paddingRight: '10px',
                  }}>
                  {labels?.policy}:
                </div>
                <strong
                  style={{
                    ...fonts.M,
                    ...fonts.Bold,
                    fontSize: '17px',
                  }}>
                  {selectedBu.grpName}
                </strong>
              </Row>
            )}
          </Column>
        </Row>
        <Row className="coverages-row coverages-list-row">
          {isLoading ? <AppSpinner></AppSpinner> : renderCoverages()}
        </Row>
      </Column>
      {!errorFetching && (
        <div>
          <NewCoverageDetails
            details={coverageDetails}
            showModal={showModal}
            setShowModal={setShowModal}
            selectedItem={selectedItem}
            deducts={deducts}
            errorFetchingDetail={errorFetchingDetail}
            setErrorFetchingDetail={setErrorFetchingDetail}
          />
        </div>
      )}
      {errorFetching && (
        <Column className="coverages-no-reimbursementsyet">
          <Icon
            className="reimbursement-history-filter-icon"
            name={Icons.ALERT_PROBLEM}
            color={colors.lightGrey}
            width="40"
            height="40"
            changeViewbox
            viewBox={'0 0 24 24'}
          />
          <h6 style={{color: colors.lightGrey, marginTop: 20}}>
            {labels?.error_fetching}
          </h6>{' '}
        </Column>
      )}
    </>
  );
}

const mapStateToProps = ({
  selectedBu,
  strings,
  globalStyles,
  titular,
  busFuncMap,
}: any) => ({
  selectedBu: selectedBu.value,
  labels: strings.value.pt.myCoverages,
  buStyles: globalStyles.value,
  titular: titular.value,
  busFuncMap: busFuncMap.value,
});

export default compose(connect(mapStateToProps)(MyCoverages));
